import React from 'react';
import '../../styles/components/AsperaHeader.scss';
import { Header, HeaderName, HeaderGlobalBar, HeaderNavigation, HeaderGlobalAction, HeaderPanel, Switcher, SwitcherItem, HeaderMenuButton, getIcon, Theme, Link } from '../Carbon';
import { withRouter } from 'react-router-dom';
import { SubManager } from '../../classes/SubManager';
import { language } from '../../services/LanguageService';
import { clearFocus } from '../../helpers/helpers';
import { getStorageItem, openNewTab, altKeysUsed, downloadSupportData, resetTableStore } from '../../helpers/starterHelpers';
import { AsperaError } from '../../classes/AsperaError';
import { getAllCurrentTransfers, getTransferSystemStatus, monitorTransfers } from '../../helpers/transfer';
import { AsperaTransferActivity } from '../AsperaTransferActivity';
import { AsperaUserMenu } from '../AsperaUserMenu';
import { storageKeys } from '../../constants/constants';
import { AsperaSearchHeader } from '../AsperaSearchHeader';
import { AsperaAppSwitcher } from '../AsperaAppSwitcher';
import { tracking } from '../../services/TrackingService';
/**
 * React Component
 *
 * The AsperaHeader renders the Carbon Top bar on the UI with custom Aspera touches.
 *
 * @example
 * ```javascript
 * <AsperaHeader />
 * ```
 */
export const AsperaHeader = withRouter(class AsperaHeader extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            activeRightMenu: undefined,
            activeRightAltKey: false,
            hasLeftNav: false,
        };
        this.subManager = new SubManager();
        /** If values set it will render the transfer monitor icon with special styling and icon and render text to left of icon */
        this.transferStatus = {
            /** Text to show on the UI next to transfer activity */
            text: undefined,
            /** The status to show on the icon */
            status: undefined,
            /** Indicate if user has opened and closed the transfer activity */
            dismissed: false,
        };
        this.closeRightMenu = () => {
            this.setState({ activeRightMenu: undefined });
        };
        this.changeApp = (appRoute, event) => {
            const { history } = this.props;
            const newTab = altKeysUsed(event);
            const destination = appRoute;
            resetTableStore();
            newTab ? openNewTab(destination) : history.push(destination);
            this.setState({ activeRightMenu: undefined });
        };
        this.setRightMenu = (key, altKey) => {
            const { activeRightMenu } = this.state;
            const { customMenuItem, notificationSystemControls } = this.props;
            if (key === activeRightMenu) {
                key = undefined;
            }
            if (key === 'notifications' && notificationSystemControls && typeof notificationSystemControls.lastSeenDateChangeCallback === 'function') {
                const now = new Date().toISOString();
                notificationSystemControls.lastSeenDate = now;
                notificationSystemControls.lastSeenDateChangeCallback(now);
            }
            if (key === 'custom') {
                if (customMenuItem === null || customMenuItem === void 0 ? void 0 : customMenuItem.onClick)
                    customMenuItem.onClick();
                key = undefined;
            }
            else {
                tracking.closeAssistMe();
            }
            if (key === 'user-profile' && this.lastMenuOpened === 'help') {
                altKey = true;
            }
            this.lastMenuOpened = key;
            this.setState({ activeRightMenu: key, activeRightAltKey: altKey });
        };
        this.closeLeftNav = () => {
            const { setLeftNav } = this.props;
            if (typeof setLeftNav === 'function')
                setLeftNav(false);
        };
        this.openLeftNav = () => {
            const { setLeftNav, leftNavExpanded } = this.props;
            if (typeof setLeftNav === 'function')
                setLeftNav(!leftNavExpanded);
        };
        this.dismissNotifications = () => {
            const { notificationSystemControls } = this.props;
            if (notificationSystemControls) {
                notificationSystemControls.lastClearDateCallback(new Date().toISOString());
            }
            this.setRightMenu(undefined, false);
        };
        this.goToAppHomeFinal = () => {
            const { currentApp, history } = this.props;
            if (currentApp.id === 'settings') {
                history.push('/');
            }
            else if (currentApp.id === 'public') {
                return;
            }
            else {
                history.push(currentApp.route);
            }
        };
        this.goToAppHome = () => {
            tracking.checkDirtyForms(() => this.goToAppHomeFinal());
        };
    }
    setTransferStatus(transferData) {
        if (transferData.transfers.length === 0)
            this.checkForNoTransferSystem();
        const originalSettings = Object.assign({}, this.transferStatus);
        let failedTransfers = 0;
        let runningTransfers = 0;
        let completeTransfers = 0;
        transferData.transfers.forEach(transfer => {
            switch (transfer.status) {
                case 'completed':
                    completeTransfers++;
                    break;
                case 'failed':
                    failedTransfers++;
                    break;
                case 'running':
                case 'partially_completed':
                case 'initiating':
                case 'waiting':
                case 'resuming':
                case 'queued':
                case 'willretry':
                    runningTransfers++;
                    break;
            }
        });
        this.transferStatus.text = undefined;
        this.transferStatus.status = undefined;
        if (failedTransfers || runningTransfers || completeTransfers) {
            const stringParts = [];
            if (completeTransfers) {
                stringParts.push(language.get('header.itemCompleted', [completeTransfers]));
                this.transferStatus.status = 'success';
            }
            if (failedTransfers) {
                stringParts.push(language.get('header.itemFailed', [failedTransfers]));
                this.transferStatus.status = 'error';
            }
            if (runningTransfers) {
                stringParts.push(language.get('header.itemTransferring', [runningTransfers]));
                this.transferStatus.status = 'active';
            }
            this.transferStatus.text = stringParts.join(', ');
        }
        if (this.transferStatus.text !== originalSettings.text || this.transferStatus.status !== originalSettings.status) {
            this.transferStatus.dismissed = false;
            this.setState({});
        }
    }
    get activityIcon() {
        if (this.transferStatus.status === 'active') {
            return getIcon('Renew', 20);
        }
        else if (this.transferStatus.status === 'unable') {
            return getIcon('Error', 20);
        }
        else {
            return getIcon('Activity', 20);
        }
    }
    get notificationSortedList() {
        const { notificationSystemControls } = this.props;
        if (notificationSystemControls && Array.isArray(notificationSystemControls.notificationList)) {
            return notificationSystemControls.notificationList.sort((a, b) => (new Date(b.date).getTime() - new Date(a.date).getTime()));
        }
        return [];
    }
    getAvailableRightOptions() {
        var _a;
        const { currentUser, hideAppSwitcher, searchData, customMenuItem, disableTransferMonitor, customUserIcon, customHelpIcon, notificationSystemControls } = this.props;
        const { activeRightMenu } = this.state;
        const imageStyle = {
            backgroundImage: `url(${currentUser.avatar_image_url})`,
        };
        return [
            {
                name: language.get('header.transferMonitor'),
                key: 'transfer-monitor',
                className: this.shouldShowTransferStatus() ? this.transferStatus.status : '',
                neverActive: true,
                icon: this.activityIcon,
                hidden: disableTransferMonitor,
            },
            {
                name: language.get('header.search'),
                key: 'search',
                icon: getIcon('Search', 20),
                hidden: !searchData,
            },
            {
                name: (customMenuItem === null || customMenuItem === void 0 ? void 0 : customMenuItem.name) || '',
                key: 'custom',
                icon: (customMenuItem === null || customMenuItem === void 0 ? void 0 : customMenuItem.icon) || null,
                hidden: !customMenuItem,
            },
            {
                name: language.get('header.help'),
                key: 'help',
                icon: customHelpIcon ? React.createElement(customHelpIcon) : getIcon('Help', 20),
            },
            {
                name: language.get('header.notifications'),
                key: 'notifications',
                hidden: !notificationSystemControls || !notificationSystemControls.notificationList.length,
                hasAlert: new Date((notificationSystemControls === null || notificationSystemControls === void 0 ? void 0 : notificationSystemControls.lastSeenDate) || 0) < new Date(((_a = this.notificationSortedList[0]) === null || _a === void 0 ? void 0 : _a.date) || 0),
                icon: getIcon('Notification', 20),
            },
            {
                name: currentUser.displayName,
                key: 'user-profile',
                icon: currentUser.avatar_image_url ? React.createElement("div", { className: "image", style: imageStyle }) : (customUserIcon ? React.createElement(customUserIcon) : getIcon('UserAvatar', 20)),
            },
            {
                name: language.get('header.appSwitcher'),
                key: 'app-switcher',
                icon: getIcon(activeRightMenu === 'app-switcher' ? 'Close' : 'Switcher', 20),
                hidden: hideAppSwitcher,
            },
        ];
    }
    getRightMenu() {
        var _a;
        const { activeRightMenu, activeRightAltKey } = this.state;
        const { currentUser, appOptions, helpOptions, settingsOnClick, preLogoutActions, onLoginClick, searchData, currentApp, changeDefaultApp } = this.props;
        if (!activeRightMenu) {
            return '';
        }
        switch (activeRightMenu) {
            case 'transfer-monitor':
                // Loaded in main component for slide animation
                return null;
            case 'user-profile':
                return React.createElement(HeaderPanel, { "aria-label": currentUser.displayName, expanded: true },
                    React.createElement(AsperaUserMenu, { preLogoutActions: preLogoutActions, user: currentUser, onClick: this.closeRightMenu, settingsOnClick: settingsOnClick, altFunctionality: activeRightAltKey, onLoginClick: onLoginClick }));
            case 'app-switcher':
                return React.createElement(AsperaAppSwitcher, { apps: appOptions, changeApp: this.changeApp, currentApp: currentApp, changeDefaultApp: changeDefaultApp });
            case 'notifications':
                return (React.createElement(HeaderPanel, { className: "notification-area", "aria-label": language.get('header.notifications'), expanded: true },
                    React.createElement("div", { className: "notification-area--header" },
                        React.createElement("div", { className: "notification-area--header--title" }, language.get('header.notifications')),
                        React.createElement(Link, { className: "notification-area--header--action", onClick: this.dismissNotifications }, language.get('header.dismissAll'))),
                    this.notificationSortedList.map((item, index) => {
                        const iconData = {
                            error: 'ErrorFilled',
                            info: 'InformationSquareFilled',
                            warn: 'WarningAltFilled',
                        };
                        return (React.createElement("div", { className: "notification-area--item", key: index },
                            React.createElement("div", { className: "notification-area--item--info" },
                                getIcon(iconData[item.type], 16, { className: `status-icon ${item.type}-icon` }),
                                React.createElement("span", null, language.getDate(item.date, 'short'))),
                            React.createElement("div", { className: "notification-area--item--title" }, item.title),
                            !!item.description && React.createElement("div", { className: "notification-area--item--description" }, item.description),
                            !!item.link && React.createElement(Link, { onClick: () => openNewTab(item.link || '', true), className: "notification-area--item--link" }, language.get('header.view'))));
                    })));
            case 'help':
                return (React.createElement(HeaderPanel, { "aria-label": language.get('header.help'), expanded: true },
                    React.createElement(Switcher, { "aria-label": language.get('header.help') },
                        helpOptions.map((option, index) => {
                            return (React.createElement(SwitcherItem, { key: index, "data-auto": `header-help-list-item-${option.route || option.displayName}`, "aria-label": option.displayName, onClick: (event) => {
                                    option.onClick ? option.onClick(option.route, event) : this.changeApp(option.route, event);
                                    this.closeRightMenu();
                                } }, option.displayName));
                        }),
                        ((_a = window.asperaLog) === null || _a === void 0 ? void 0 : _a.length) && (React.createElement(SwitcherItem, { key: "download-debug", "data-auto": "header-help-list-item-download-debug", "aria-label": language.get('header.downloadSupportData'), onClick: () => {
                                downloadSupportData();
                                this.closeRightMenu();
                            } }, language.get('header.downloadSupportData'))))));
            case 'search':
                return searchData ? (React.createElement(HeaderPanel, { className: "aspera-header-panel--extra-large", "aria-label": language.get('header.search'), expanded: true },
                    React.createElement(AsperaSearchHeader, Object.assign({}, searchData, { onClick: this.closeRightMenu })))) : null;
        }
    }
    shouldShowTransferStatus() {
        const { disableTransferMonitor } = this.props;
        return !!(!disableTransferMonitor && this.transferStatus.text && (!this.transferStatus.dismissed || this.transferStatus.status === 'active'));
    }
    getHeader() {
        const { brandingOptions, productName } = this.props;
        if (brandingOptions && ((brandingOptions === null || brandingOptions === void 0 ? void 0 : brandingOptions.imageUrl) || (brandingOptions === null || brandingOptions === void 0 ? void 0 : brandingOptions.customText))) {
            return (React.createElement("span", { className: "clickable-product-name", onClick: this.goToAppHome, "data-auto": "header-logo" },
                React.createElement(HeaderName, { className: "product-name org-header-image" },
                    !!brandingOptions.imageUrl && React.createElement("img", { className: "org-header-image--container", title: brandingOptions.displayName || '', alt: brandingOptions.displayName || '', src: brandingOptions.imageUrl }),
                    !!brandingOptions.customText && brandingOptions.customText)));
        }
        else {
            return (React.createElement("span", { className: "clickable-product-name", onClick: this.goToAppHome, "data-auto": "header-logo" },
                React.createElement(HeaderName, { className: "product-name", prefix: language.get('globals.ibm') }, productName)));
        }
    }
    checkForNoTransferSystem() {
        const dismissed = !!getStorageItem(storageKeys.skipTransferSystem);
        if (dismissed) {
            if (getTransferSystemStatus().type === 'none' && this.transferStatus.status === undefined) {
                this.transferStatus.status = 'unable';
                this.transferStatus.text = language.get('transferSystem.transferUnavailable');
                this.transferStatus.dismissed = false;
                this.setState({});
            }
            else if (getTransferSystemStatus().type !== 'none' && this.transferStatus.status === 'unable') {
                this.transferStatus.status = undefined;
                this.transferStatus.text = undefined;
                this.transferStatus.dismissed = false;
                this.setState({});
            }
        }
    }
    checkForTransfers() {
        if (this.lastTransferSystem !== getTransferSystemStatus().type) {
            this.lastTransferSystem = getTransferSystemStatus().type;
            this.subManager.setSubscription(getAllCurrentTransfers().subscribe({
                next: transfers => {
                    if (transfers.result_count) {
                        this.setTransferStatus(transfers);
                    }
                },
                error: error => {
                    new AsperaError('AsperaHeader: Unable to get all transfers', error);
                },
            }));
        }
    }
    componentDidMount() {
        const { leftNavProps } = this.props;
        if (this.transferStatus.status) {
            this.setState({});
        }
        this.subManager.setSubscription(monitorTransfers().subscribe({
            next: transferData => {
                this.setTransferStatus(transferData);
            },
            error: error => {
                new AsperaError('AsperaHeader: Transfer monitor encountered an error', error);
            },
        }));
        this.checkForTransfers();
        if (leftNavProps && leftNavProps.itemsSubscriber) {
            this.subManager.setSubscription(leftNavProps.itemsSubscriber.subscribe({
                next: items => {
                    this.setState({ hasLeftNav: !!items.length });
                },
                error: error => {
                    new AsperaError('AsperaHeader: unable to watch left nav updates', error);
                },
            }));
        }
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    componentDidUpdate() {
        this.checkForNoTransferSystem();
        this.checkForTransfers();
    }
    render() {
        const { className, productName, currentApp, leftNavExpanded, transferMonitorExtraOptions } = this.props;
        const { activeRightMenu, hasLeftNav } = this.state;
        return (React.createElement(Theme, { theme: "g100", className: `aspera-header ${className ? className : ''}` },
            React.createElement(Header, { "aria-label": productName },
                !!hasLeftNav && React.createElement(HeaderMenuButton, { className: "mobile-left-nav", isActive: leftNavExpanded, "aria-label": language.get('header.openLeftNav'), onClick: this.openLeftNav, "data-auto": "header-menu-mobile-toggle" }),
                this.getHeader(),
                React.createElement(HeaderNavigation, { "aria-label": currentApp.displayName || language.get('navItems.home') },
                    React.createElement("span", { className: "app-name" }, currentApp.displayName || language.get('navItems.home'))),
                React.createElement(HeaderGlobalBar, null,
                    this.shouldShowTransferStatus() ? React.createElement(HeaderNavigation, { className: "header-transfer-text", "aria-label": this.transferStatus.text || language.get('header.transferMonitor') },
                        React.createElement("span", null, this.transferStatus.text)) : '',
                    this.getAvailableRightOptions().filter(option => !option.hidden).map((option, index) => {
                        return (React.createElement(HeaderGlobalAction, { className: `header-icon-item ${option.className ? option.className : ''}`, "data-auto": `header-right-item-${option.key}`, isActive: option.neverActive ? false : option.key === activeRightMenu, key: index, "aria-label": option.name, tooltipAlignment: "end", onClick: (event) => {
                                this.closeLeftNav();
                                clearFocus();
                                this.setRightMenu(option.key, altKeysUsed(event));
                            } },
                            option.icon,
                            option.hasAlert && React.createElement("div", { className: "header-alert" })));
                    })),
                React.createElement(AsperaTransferActivity, { onClose: this.closeRightMenu, extraOptions: transferMonitorExtraOptions, open: activeRightMenu === 'transfer-monitor' }),
                this.getRightMenu())));
    }
});
